import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { marketRatesAPI } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { TrendingUp, Plus, Edit2, RefreshCw } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';

export default function MarketRates() {
  const queryClient = useQueryClient();
  const [selectedRegion, setSelectedRegion] = useState('ALL');
  const [editingRate, setEditingRate] = useState(null);
  const [newPrice, setNewPrice] = useState('');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newRate, setNewRate] = useState({
    source: '',
    iso_region: 'PJM',
    rate_type: 'Energy',
    price: '',
    effective_date: new Date().toISOString().split('T')[0],
  });

  const { data: rates, isLoading, refetch } = useQuery({
    queryKey: ['market-rates', selectedRegion],
    queryFn: () =>
      marketRatesAPI
        .getAll({ iso_region: selectedRegion !== 'ALL' ? selectedRegion : undefined })
        .then((res) => res.data),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, price }) => marketRatesAPI.update(id, parseFloat(price)),
    onSuccess: () => {
      queryClient.invalidateQueries(['market-rates']);
      setEditingRate(null);
      setNewPrice('');
      toast.success('Market rate updated successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to update rate');
    },
  });

  const createMutation = useMutation({
    mutationFn: (data) => marketRatesAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['market-rates']);
      setShowAddDialog(false);
      setNewRate({
        source: '',
        iso_region: 'PJM',
        rate_type: 'Energy',
        price: '',
        effective_date: new Date().toISOString().split('T')[0],
      });
      toast.success('Market rate created successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to create rate');
    },
  });

  const handleUpdate = (rateId) => {
    if (!newPrice || parseFloat(newPrice) <= 0) {
      toast.error('Please enter a valid price');
      return;
    }
    updateMutation.mutate({ id: rateId, price: newPrice });
  };

  const handleCreate = (e) => {
    e.preventDefault();
    if (!newRate.source || !newRate.price || parseFloat(newRate.price) <= 0) {
      toast.error('Please fill all required fields');
      return;
    }
    createMutation.mutate({
      ...newRate,
      price: parseFloat(newRate.price),
      effective_date: new Date(newRate.effective_date).toISOString(),
    });
  };

  const isoRegions = ['ALL', 'PJM', 'ERCOT', 'CAISO', 'NYISO', 'ISONE', 'MISO', 'SPP'];

  return (
    <div className="p-8 space-y-6" data-testid="market-rates">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold flex items-center" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <TrendingUp className="w-10 h-10 mr-3 text-[#09409C]" />
            <span className="text-gradient-primary">Market Rates</span>
          </h1>
          <p className="text-muted-foreground mt-2 text-lg">
            View and manage market rates for energy benchmarking
          </p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => refetch()} data-testid="refresh-rates-button">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button
                className="bg-gradient-to-r from-[#09409C] to-[#146CBB] hover:from-[#072e70] hover:to-[#105a99]"
                data-testid="add-rate-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Rate
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="add-rate-dialog">
              <DialogHeader>
                <DialogTitle>Add Market Rate</DialogTitle>
                <DialogDescription>
                  Create a new market rate entry for benchmarking
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="source">Source</Label>
                  <Input
                    id="source"
                    placeholder="e.g., ICE, CME, EIA"
                    value={newRate.source}
                    onChange={(e) => setNewRate({ ...newRate, source: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="iso_region">ISO Region</Label>
                  <Select
                    value={newRate.iso_region}
                    onValueChange={(value) => setNewRate({ ...newRate, iso_region: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {isoRegions.filter((r) => r !== 'ALL').map((region) => (
                        <SelectItem key={region} value={region}>
                          {region}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="rate_type">Rate Type</Label>
                  <Input
                    id="rate_type"
                    placeholder="e.g., Energy, Capacity"
                    value={newRate.rate_type}
                    onChange={(e) => setNewRate({ ...newRate, rate_type: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="price">Price ($/MWh)</Label>
                  <Input
                    id="price"
                    type="number"
                    step="0.01"
                    placeholder="50.00"
                    value={newRate.price}
                    onChange={(e) => setNewRate({ ...newRate, price: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="effective_date">Effective Date</Label>
                  <Input
                    id="effective_date"
                    type="date"
                    value={newRate.effective_date}
                    onChange={(e) => setNewRate({ ...newRate, effective_date: e.target.value })}
                    required
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-[#09409C] to-[#146CBB]"
                  disabled={createMutation.isPending}
                >
                  {createMutation.isPending ? 'Creating...' : 'Create Rate'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-2xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Current Market Rates
              </CardTitle>
              <CardDescription className="mt-2">
                Latest rates from various market sources across ISO regions
              </CardDescription>
            </div>
            <Select value={selectedRegion} onValueChange={setSelectedRegion}>
              <SelectTrigger className="w-[180px]" data-testid="region-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {isoRegions.map((region) => (
                  <SelectItem key={region} value={region}>
                    {region === 'ALL' ? 'All Regions' : region}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Source</TableHead>
                  <TableHead>ISO Region</TableHead>
                  <TableHead>Rate Type</TableHead>
                  <TableHead>Price ($/MWh)</TableHead>
                  <TableHead>Effective Date</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="h-64 text-center">
                      <RefreshCw className="w-6 h-6 animate-spin mx-auto text-[#09409C]" />
                      <p className="mt-2">Loading market rates...</p>
                    </TableCell>
                  </TableRow>
                ) : rates && rates.length > 0 ? (
                  rates.map((rate) => (
                    <TableRow key={rate.id} data-testid={`rate-row-${rate.id}`}>
                      <TableCell className="font-medium">{rate.source}</TableCell>
                      <TableCell>
                        <span className="px-2 py-1 rounded-md text-xs font-semibold bg-[#09409C] text-white">
                          {rate.iso_region}
                        </span>
                      </TableCell>
                      <TableCell>{rate.rate_type}</TableCell>
                      <TableCell>
                        {editingRate === rate.id ? (
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              step="0.01"
                              value={newPrice}
                              onChange={(e) => setNewPrice(e.target.value)}
                              className="w-24"
                              data-testid={`edit-price-input-${rate.id}`}
                            />
                            <Button
                              size="sm"
                              onClick={() => handleUpdate(rate.id)}
                              disabled={updateMutation.isPending}
                            >
                              Save
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setEditingRate(null);
                                setNewPrice('');
                              }}
                            >
                              Cancel
                            </Button>
                          </div>
                        ) : (
                          <span className="font-semibold">${rate.price.toFixed(2)}</span>
                        )}
                      </TableCell>
                      <TableCell>{new Date(rate.effective_date).toLocaleDateString()}</TableCell>
                      <TableCell>
                        {editingRate !== rate.id && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setEditingRate(rate.id);
                              setNewPrice(rate.price.toString());
                            }}
                            data-testid={`edit-rate-button-${rate.id}`}
                          >
                            <Edit2 className="w-3 h-3 mr-1" />
                            Edit
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} className="h-64 text-center text-muted-foreground">
                      <TrendingUp className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                      <p>No market rates found</p>
                      <p className="text-sm mt-2">Add your first market rate to get started</p>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
            About Market Rates
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-sm text-muted-foreground">
          <p>
            Market rates are used to benchmark contract pricing and calculate competitiveness scores. These rates
            represent current market conditions across different ISO regions.
          </p>
          <div className="space-y-2">
            <p className="font-semibold text-foreground">Common Sources:</p>
            <ul className="list-disc list-inside space-y-1 pl-4">
              <li>ICE (Intercontinental Exchange) - Natural gas and power futures</li>
              <li>CME Group - Henry Hub natural gas, PJM Western Hub power</li>
              <li>EIA (Energy Information Administration) - Monthly/annual price indices</li>
              <li>ISO Real-Time Prices - Direct from grid operators</li>
            </ul>
          </div>
          <p className="italic">
            Note: In Phase 1, rates are managed manually. Future phases will include automated data feeds from market sources.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
